/**
 * creature/behavior.ts — How Things Move Through the World
 *
 * The behavioral state machine: each creature cycles through states
 * (idle, sleep, wander, follow, play, eat, groom, explore, flee...) based on
 * probabilistic transitions weighted by genome traits, needs, room mood,
 * player proximity, and time of day.
 *
 * This is the generalized form of title.ts _updateCatState — same pattern
 * (probabilistic weighted transitions, lerp blending toward target poses),
 * but driven by the behavioral genome instead of hardcoded probabilities.
 */

import type { BehaviorGenome } from "./genome.js";
import type { CreaturePose } from "./pose.js";
import { applyPoseOverride, zeroPose } from "./pose.js";
import { activityLevel } from "./genome.js";

// ─── Behavior States ────────────────────────────────────────────────────────

export type CreatureBehaviorState =
  | "idle"        // standing, looking around
  | "sleep"       // curled up, eyes closed
  | "wander"      // moving to a random walkable tile
  | "follow"      // following the owner
  | "play"        // playing with toy, animal, or player
  | "eat"         // eating from food bowl
  | "drink"       // drinking from water source
  | "groom"       // self-grooming
  | "explore"     // investigating new object
  | "flee"        // running from something scary
  | "socialize"   // interacting with another creature
  | "beg"         // begging for attention/food
  | "patrol"      // moving along a route
  | "sit"         // sitting on command or near owner
  | "scratch"     // scratching self
  | "yawn"        // yawning/stretching
  | "mounted"     // being ridden (AI suspended)
  | "chase";      // pursuing a hostile target (combat)

// ─── Creature Needs ─────────────────────────────────────────────────────────

export interface CreatureNeeds {
  hunger: number;     // 0 = starving, 1 = full
  happiness: number;  // 0 = miserable, 1 = joyful
  energy: number;     // 0 = exhausted, 1 = rested
  hygiene: number;    // 0 = dirty, 1 = clean
  social: number;     // 0 = lonely, 1 = satisfied
}

export function defaultNeeds(): CreatureNeeds {
  return { hunger: 0.8, happiness: 0.7, energy: 0.9, hygiene: 0.8, social: 0.5 };
}

// ─── Behavior State Object ──────────────────────────────────────────────────

export interface BehaviorState {
  current: CreatureBehaviorState;
  stateTime: number;           // seconds in current state
  nextTransition: number;      // seconds until forced transition
  target?: { tileX: number; tileY: number };
  interactionTarget?: string;  // ID of creature/furniture being interacted with

  // Spawn point — for leash/evade mechanic (creature resets if pulled too far)
  spawnTileX?: number;
  spawnTileY?: number;

  // Memory
  favoriteSpots: Array<{ tileX: number; tileY: number; score: number }>;
  fearedObjects: Set<string>;
  bondedCreatures: Set<string>;
  lastFedTime: number;
  lastPettedTime: number;
  lastPlayedTime: number;
}

export function defaultBehaviorState(): BehaviorState {
  return {
    current: "sleep",
    stateTime: 0,
    nextTransition: 5 + Math.random() * 15,
    favoriteSpots: [],
    fearedObjects: new Set(),
    bondedCreatures: new Set(),
    lastFedTime: 0,
    lastPettedTime: 0,
    lastPlayedTime: 0,
  };
}

// ─── Room Mood (simplified interface to avoid circular deps) ────────────────

interface RoomMoodInput {
  warmth: number;
  energy: number;
  mystery: number;
  noise: number;
  nature: number;
}

// ─── State Transition Engine ────────────────────────────────────────────────

interface TransitionContext {
  genome: BehaviorGenome;
  needs: CreatureNeeds;
  mood: RoomMoodInput;
  gameHour: number;
  ownerNearby: boolean;
  ownerDistance: number;
  otherCreatureCount: number;
  bondLevel: number;
}

/**
 * Evaluate a state transition. Returns the new state and duration.
 * This is the core AI — probabilistic weights from genome + context.
 */
export function evaluateTransition(
  current: CreatureBehaviorState,
  ctx: TransitionContext
): { state: CreatureBehaviorState; duration: number } {
  const g = ctx.genome;
  const n = ctx.needs;
  const activity = activityLevel(g, ctx.gameHour);

  // Build weighted candidates
  const candidates: Array<{ state: CreatureBehaviorState; weight: number; duration: [number, number] }> = [];

  // Universal transitions available from most states
  if (current !== "sleep" && n.energy < 0.3) {
    candidates.push({ state: "sleep", weight: 3.0, duration: [10, 30] });
  }
  if (current !== "eat" && n.hunger < 0.3) {
    candidates.push({ state: "eat", weight: 2.5, duration: [3, 6] });
  }
  if (current !== "flee" && ctx.mood.noise > 0.7 && g.courage < 0.3) {
    candidates.push({ state: "flee", weight: 2.0, duration: [2, 5] });
  }

  // State-specific transitions
  switch (current) {
    case "sleep":
      candidates.push(
        { state: "idle",    weight: 0.3 + activity * 0.5, duration: [3, 8] },
        { state: "yawn",    weight: 0.4, duration: [2, 4] },
        { state: "groom",   weight: 0.2 + (1 - n.hygiene) * 0.3, duration: [3, 6] },
        { state: "scratch", weight: 0.15, duration: [2, 4] },
      );
      break;

    case "idle":
      candidates.push(
        { state: "sleep",   weight: 0.3 * (1 - activity), duration: [8, 25] },
        { state: "sit",     weight: 0.25, duration: [4, 10] },
        { state: "wander",  weight: 0.2 + g.curiosity * 0.3, duration: [3, 8] },
        { state: "explore", weight: g.curiosity * 0.3, duration: [3, 6] },
        { state: "groom",   weight: 0.15 + (1 - n.hygiene) * 0.2, duration: [3, 6] },
        { state: "scratch", weight: 0.1, duration: [2, 3] },
        { state: "yawn",    weight: 0.1 * (1 - activity), duration: [2, 3] },
      );
      if (ctx.ownerNearby) {
        candidates.push(
          { state: "follow",  weight: g.sociability * 0.4, duration: [5, 15] },
          { state: "beg",     weight: g.sociability * 0.2 * (1 - n.hunger), duration: [3, 6] },
          { state: "play",    weight: g.playfulness * 0.3, duration: [4, 10] },
        );
      }
      if (ctx.otherCreatureCount > 0) {
        candidates.push(
          { state: "socialize", weight: g.packInstinct * 0.3, duration: [4, 8] },
        );
      }
      break;

    case "sit":
      candidates.push(
        { state: "idle",    weight: 0.3, duration: [3, 7] },
        { state: "sleep",   weight: 0.2 * (1 - activity), duration: [8, 20] },
        { state: "wander",  weight: 0.25 + g.curiosity * 0.2, duration: [3, 6] },
        { state: "groom",   weight: 0.1, duration: [3, 5] },
        { state: "scratch", weight: 0.1, duration: [2, 3] },
      );
      break;

    case "wander":
      candidates.push(
        { state: "idle",    weight: 0.3, duration: [3, 8] },
        { state: "sit",     weight: 0.25, duration: [4, 8] },
        { state: "sleep",   weight: 0.15 * (1 - activity), duration: [8, 20] },
        { state: "explore", weight: g.curiosity * 0.3, duration: [3, 6] },
      );
      break;

    case "follow":
      candidates.push(
        { state: "idle",    weight: 0.2, duration: [3, 6] },
        { state: "sit",     weight: 0.3, duration: [5, 10] },
        { state: "play",    weight: g.playfulness * 0.3, duration: [4, 8] },
        { state: "beg",     weight: g.sociability * 0.15, duration: [2, 5] },
      );
      break;

    case "play":
      candidates.push(
        { state: "idle",    weight: 0.3, duration: [3, 6] },
        { state: "sit",     weight: 0.2, duration: [4, 8] },
        { state: "wander",  weight: 0.2, duration: [3, 5] },
      );
      break;

    case "groom":
    case "scratch":
    case "yawn":
      // Short states — return to idle or sleep
      candidates.push(
        { state: "idle",    weight: 0.4 + activity * 0.2, duration: [3, 8] },
        { state: "sleep",   weight: 0.3 * (1 - activity), duration: [8, 20] },
        { state: "sit",     weight: 0.2, duration: [4, 8] },
      );
      break;

    case "eat":
    case "drink":
      candidates.push(
        { state: "idle",    weight: 0.4, duration: [3, 6] },
        { state: "sleep",   weight: 0.2, duration: [8, 15] },
        { state: "wander",  weight: 0.2, duration: [3, 5] },
      );
      break;

    case "explore":
      candidates.push(
        { state: "idle",    weight: 0.3, duration: [3, 6] },
        { state: "wander",  weight: 0.3, duration: [3, 6] },
        { state: "sit",     weight: 0.2, duration: [4, 8] },
      );
      break;

    case "flee":
      candidates.push(
        { state: "idle",    weight: 0.5, duration: [3, 6] },
        { state: "sleep",   weight: 0.2, duration: [5, 12] },
      );
      break;

    case "beg":
    case "socialize":
    case "patrol":
      candidates.push(
        { state: "idle",    weight: 0.4, duration: [3, 8] },
        { state: "sit",     weight: 0.2, duration: [4, 8] },
        { state: "wander",  weight: 0.2, duration: [3, 5] },
      );
      break;

    default:
      candidates.push({ state: "idle", weight: 1, duration: [3, 8] });
  }

  // Weighted random selection
  const totalWeight = candidates.reduce((sum, c) => sum + c.weight, 0);
  let r = Math.random() * totalWeight;
  for (const c of candidates) {
    r -= c.weight;
    if (r <= 0) {
      const duration = c.duration[0] + Math.random() * (c.duration[1] - c.duration[0]);
      return { state: c.state, duration };
    }
  }

  // Fallback
  return { state: "idle", duration: 5 };
}

// ─── Pose Targets Per State ─────────────────────────────────────────────────

/**
 * Get the target pose for a behavior state.
 * Generalizes title.ts _catTargetForState.
 */
export function poseForState(
  state: CreatureBehaviorState,
  time: number
): Partial<CreaturePose> {
  switch (state) {
    case "sleep":
      return { spineAngle: 0, headLift: 0, headTurn: 0, jawOpen: 0, eyeOpen: 0, tailWag: 0, scratchLeg: 0, tailCurl: 0.3 };
    case "idle":
      return { spineAngle: 0.05, headLift: 0.5, eyeOpen: 1, tailWag: 0.05, scratchLeg: 0 };
    case "sit":
      return { spineAngle: 0.6, headLift: 0.8, headTurn: 0, eyeOpen: 1, tailCurl: 0.5, tailWag: 0.1, scratchLeg: 0 };
    case "wander":
      return { spineAngle: 0.45, headLift: 0.7, eyeOpen: 1, tailWag: 0.25, tailCurl: 0.4, scratchLeg: 0 };
    case "follow":
      return { spineAngle: 0.4, headLift: 0.75, eyeOpen: 1, tailWag: 0.4, tailCurl: 0.3, scratchLeg: 0 };
    case "play":
      // Play bow — front low, rear high, tail going wild
      return { spineAngle: 0.2, headLift: 0.4, eyeOpen: 1, tailWag: 0.8, tailCurl: 0.2, jawOpen: Math.sin(time * 3) > 0.7 ? 0.3 : 0, scratchLeg: 0 };
    case "eat":
    case "drink":
      return { spineAngle: 0.35, headLift: 0.1, eyeOpen: 0.8, tailWag: 0.05, scratchLeg: 0 };
    case "groom":
      return { spineAngle: 0.3, headLift: 0.2, headTurn: -0.6, eyeOpen: 0.5, jawOpen: 0.15, tailWag: 0, tailCurl: 0.6, scratchLeg: 0 };
    case "scratch":
      return { spineAngle: 0.35, headLift: 0.4, headTurn: -0.4, eyeOpen: 0.6, scratchLeg: 1, tailWag: 0.05 };
    case "yawn":
      // Yawn + stretch — head lifts, back arches, big yawn, front paws extend
      return { spineAngle: 0.15, headLift: 0.7, jawOpen: 1, eyeOpen: 0.15, tailWag: 0, tailCurl: 0.8, scratchLeg: 0 };
    case "explore":
      // Nose-forward, alert, head swiveling to investigate
      return { spineAngle: 0.3, headLift: 0.55, headTurn: Math.sin(time * 0.7) * 0.5, eyeOpen: 1, tailWag: 0.2, tailCurl: 0.5, earTwitchL: Math.sin(time * 2.1) * 0.4, earTwitchR: Math.sin(time * 1.7) * 0.35, scratchLeg: 0 };
    case "flee":
      return { spineAngle: 0.5, headLift: 0.8, eyeOpen: 1, tailWag: 0, tailCurl: 0.1, scratchLeg: 0 };
    case "beg":
      // Sitting up, looking at owner, tail wagging eagerly, slight head tilt
      return { spineAngle: 0.7, headLift: 0.95, eyeOpen: 1, tailWag: 0.7, headTurn: Math.sin(time * 0.8) * 0.25, scratchLeg: 0 };
    case "socialize":
      return { spineAngle: 0.4, headLift: 0.6, eyeOpen: 1, tailWag: 0.35, scratchLeg: 0 };
    case "patrol":
      return { spineAngle: 0.4, headLift: 0.7, eyeOpen: 1, tailWag: 0.1, scratchLeg: 0 };
    case "mounted":
      return { spineAngle: 0.4, headLift: 0.7, eyeOpen: 1, tailWag: 0.1, scratchLeg: 0 };
    case "chase":
      // Predatory pursuit — head low, body leaned forward, tail straight, locked eyes
      return { spineAngle: 0.25, headLift: 0.35, headTurn: 0, eyeOpen: 1, tailWag: 0, tailCurl: 0.05, jawOpen: 0.15, scratchLeg: 0 };
    default:
      return {};
  }
}

// ─── Needs Decay ────────────────────────────────────────────────────────────

/** Decay rates per second for each need */
const NEED_DECAY: Record<keyof CreatureNeeds, number> = {
  hunger: 0.002,      // ~8 minutes from full to empty
  happiness: 0.001,   // ~16 minutes
  energy: 0.0015,     // ~11 minutes
  hygiene: 0.0008,    // ~20 minutes
  social: 0.001,      // ~16 minutes
};

/** Update needs based on current state and time */
export function updateNeeds(
  needs: CreatureNeeds,
  state: CreatureBehaviorState,
  dt: number
): void {
  // Base decay
  needs.hunger = Math.max(0, needs.hunger - NEED_DECAY.hunger * dt);
  needs.happiness = Math.max(0, needs.happiness - NEED_DECAY.happiness * dt);
  needs.hygiene = Math.max(0, needs.hygiene - NEED_DECAY.hygiene * dt);
  needs.social = Math.max(0, needs.social - NEED_DECAY.social * dt);

  // State-specific effects
  switch (state) {
    case "sleep":
      needs.energy = Math.min(1, needs.energy + 0.005 * dt);
      break;
    case "wander":
    case "follow":
    case "flee":
    case "patrol":
    case "chase":
      needs.energy = Math.max(0, needs.energy - 0.003 * dt);
      break;
    case "play":
      needs.energy = Math.max(0, needs.energy - 0.005 * dt);
      needs.happiness = Math.min(1, needs.happiness + 0.003 * dt);
      break;
    case "eat":
      needs.hunger = Math.min(1, needs.hunger + 0.02 * dt);
      needs.happiness = Math.min(1, needs.happiness + 0.001 * dt);
      break;
    case "drink":
      needs.hunger = Math.min(1, needs.hunger + 0.005 * dt);
      break;
    case "groom":
      needs.hygiene = Math.min(1, needs.hygiene + 0.008 * dt);
      break;
    case "socialize":
      needs.social = Math.min(1, needs.social + 0.004 * dt);
      needs.happiness = Math.min(1, needs.happiness + 0.002 * dt);
      break;
    default:
      needs.energy = Math.max(0, needs.energy - 0.001 * dt);
  }
}

// ─── Procedural Animation Layers ────────────────────────────────────────────

/**
 * Apply procedural animation layers on top of the base pose.
 * Ear twitches, walk cycle, breathing — the details that make it alive.
 * Generalizes the procedural animation code from title.ts lines 1377-1393.
 */
export function applyProceduralLayers(
  pose: CreaturePose,
  state: CreatureBehaviorState,
  time: number,
  dt: number,
  walkDir: number
): void {
  // Ear twitches — procedural noise layered on top
  pose.earTwitchL = Math.sin(time * 7.3 + 1.2) * 0.15 * pose.headLift
    + (state === "idle" || state === "explore" ? Math.sin(time * 11) * 0.3 : 0);
  pose.earTwitchR = Math.sin(time * 6.1 + 3.4) * 0.12 * pose.headLift
    + (state === "idle" || state === "explore" ? Math.sin(time * 9 + 2) * 0.25 : 0);

  // Breathing phase advances
  pose.breathePhase += dt * (pose.eyeOpen > 0.5 ? 1.4 : 0.85);

  // Walk cycle
  const isMoving = state === "wander" || state === "follow" || state === "patrol"
    || state === "flee" || state === "explore" || state === "chase";
  if (isMoving) {
    const walkSpeed = state === "flee" ? 8 : state === "chase" ? 9 : 5;
    pose.walkPhase = (pose.walkPhase + dt * walkSpeed) % (Math.PI * 2);
    pose.walkOffset.x += dt * 0.08 * walkDir;
    pose.walkOffset.x = Math.max(-0.18, Math.min(0.18, pose.walkOffset.x));
  } else {
    // Decay walk offset when stopped
    pose.walkOffset.x *= (1 - dt * 2);
    if (Math.abs(pose.walkOffset.x) < 0.003) pose.walkOffset.x = 0;
    pose.walkOffset.y *= (1 - dt * 2);
    if (Math.abs(pose.walkOffset.y) < 0.003) pose.walkOffset.y = 0;
  }
}
